import { useState, useReducer, useRef, useEffect, useCallback } from "react";
import { Stack, TextField, ActionButton, Callout, DirectionalHint, TooltipHost, Checkbox, PrimaryButton, IDetailsHeaderProps } from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import { Send28Filled, Stop24Filled, List28Regular, PanelLeftExpand24Regular } from "@fluentui/react-icons";
import { DetailsList, Selection, SelectionMode, IColumn } from "@fluentui/react";
import { GoMultiSelect } from "react-icons/go";
import styles from "./QuestionInput.module.css";
import { ClearChatButton } from "../../components/ClearChatButton";
import { TogglePanelButton } from "../../components/TogglePanelButton";
import ChatControls from "../../components/ChatControls/ChatControls";
import { AddURLs } from "../../components/AddURLs";
import { onRenderDetailsHeader } from "../../components/detailedList/detailedMdls";
import { gselectedgroup, gtiers, gloggedinuser, gqasimplified, gfileuserselect } from "../../interfaces";
import { actionButtonStyles, clipIcon, questionInputUploadCallout, questionInputTextArea } from "../../styles_glob";

import SpeechRecognizer from "../../components/SpeechRecognizer/SpeechRecognizer";
import { adjustHeight } from "../../util_glob";

interface Props {
    clearOnSend?: boolean;
    isStreaming: boolean;
    disabled: boolean;
    placeholder?: string;
    permissionLevel: number;
    selectedGroup: gselectedgroup;
    lTiers: gtiers[];
    msgLmtHit: string;
    loggedInUser: gloggedinuser;
    onSend: (
        question: string,
        q_seq_id: number,
        current_intent: string,
        ignore_discard?: boolean,
        additional_string_data?: string,
        msgovrride?: string
    ) => void;
    updateCatsState: (question: string) => void;
    isCancellingMidway: () => void;
    toggleLeftPanel: () => void;
    newConverson: () => void;
    listRefreshRequest: () => void;
    isOnCallback: (isOn: boolean) => void;
    convoEnd: (question_and_answers_list: gqasimplified[], convokey: string) => void;
    isSpeaking?: boolean;
    isSpeechPaused: boolean | null;
    lastMessage: string[];
    activeConvoKey: string;
    question_and_answers_list: gqasimplified[];
    convo_discard_stamp: number;
    fileselection: gfileuserselect[];
    handleSelectedFids: (fids: gfileuserselect[]) => void;
    onCitationClicked: (filePath: string, integratedCitation: string | null) => void;
}

export const QuestionInput = ({
    clearOnSend,
    isStreaming,
    disabled,
    placeholder,
    permissionLevel,
    selectedGroup,
    lTiers,
    msgLmtHit,
    loggedInUser,
    onSend,
    updateCatsState,
    isCancellingMidway,
    toggleLeftPanel,
    newConverson,
    listRefreshRequest,
    isOnCallback,
    convoEnd,
    isSpeaking,
    isSpeechPaused,
    lastMessage,
    activeConvoKey,
    question_and_answers_list,
    convo_discard_stamp,
    fileselection,
    handleSelectedFids,
    onCitationClicked
}: Props) => {
    const [chatControlsOpen, setChatControlsOpen] = useState<boolean>(false);
    const [isFileUploadDiagOpen, setIsFileUploadDiagOpen] = useState<boolean>(false);
    const [isFileMultiSelectCalloutOpen, setIsFileMultiSelectCalloutOpen] = useState<boolean>(false);
    const [iterQ, SetIterQ] = useState<number>(0);
    const [isSpeakingState, setIsSpeakingState] = useState<boolean>(isSpeaking || false);
    const [isOn, setIsOn] = useState<boolean>(false);
    const [fileMultiSelect, setFileMultiSelect] = useState<gfileuserselect[]>(fileselection ? fileselection.map(f => ({ ...f })) : []);
    const qRef = useRef<string>("");
    const convRef = useRef<string>("");
    const textAreaRef = useRef(null);
    const uploadButRef = useRef(null);
    const sendButRef = useRef(null);
    const convDivRef = useRef(null);
    const butContainerRef = useRef(null);
    const q_sequential_ref = useRef<number>(0);
    const ignore_discard_ref = useRef<boolean>(false);
    const additional_instr = useRef<string>("");
    const msgovrride_ref = useRef<string>("");
    const intent_ref = useRef<string>("");
    const [, forceRender] = useReducer(x => x + 1, 0);
    const fileMultiSelectButRef = useRef<HTMLDivElement>(null);
    const fileColumns: IColumn[] = [
        {
            key: "name",
            name: "File Name",
            fieldName: "name",
            minWidth: 120,
            maxWidth: 300,
            isResizable: true
        },
        {
            key: "doc_type",
            name: "Type",
            fieldName: "doc_category",
            minWidth: 60,
            maxWidth: 120,
            isResizable: true
        }
    ];

    const selectionRef = useRef<Selection>();
    if (!selectionRef.current) {
        selectionRef.current = new Selection({
            selectionMode: SelectionMode.multiple,
            onSelectionChanged: () => {
                const selectedItems = selectionRef.current!.getSelection() as gfileuserselect[];
                setFileMultiSelect(prev => prev.map(f => ({ ...f, selected: selectedItems.some(s => s.fileid === f.fileid) })));
            }
        });
    }
    useEffect(() => {
        if (selectionRef.current && fileMultiSelect) {
            fileMultiSelect.forEach((f, idx) => {
                selectionRef.current!.setIndexSelected(idx, !!f.selected, false);
            });
        }
    }, [fileMultiSelect]);

    useEffect(() => {
        setIsSpeakingState(isSpeaking || false);
    }, [isSpeaking]);

    useEffect(() => {
        if (fileselection) {
            setFileMultiSelect(fileselection.map(f => ({ ...f })));
        }
    }, [fileselection]);

    const onInputChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setQ(newValue || "");
    };
    const setQ = (newValue: string) => {
        !newValue ? (qRef.current = "") : (qRef.current = newValue);
        adjustHeight(styles.questionInputContainer);
        forceRender();
    };
    const setConvQ = (newValue: string) => {
        convRef.current = newValue;
        adjustHeight(styles.questionInputContainer);
        forceRender();
    };
    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQ();
        }
    };
    const simulateSendClick = () => {
        const event = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window
        });
        if (convDivRef.current) (convDivRef.current as any).dispatchEvent(event);
    };

    const sendQ = () => {
        if (disabled || !qRef.current.trim()) return;
        q_sequential_ref.current++;

        onSend(
            qRef.current,
            q_sequential_ref.current,
            window.global_expected_next_user_response_type,
            ignore_discard_ref.current,
            additional_instr.current || ""
        );
        if (clearOnSend) setQ("");
    };

    const sendConv = () => {
        if (!convRef.current.trim()) return;
        q_sequential_ref.current++;
        console.log("📤", convRef.current, ignore_discard_ref.current);
        onSend(
            convRef.current,
            q_sequential_ref.current,
            intent_ref.current,
            ignore_discard_ref.current,
            additional_instr.current || "",
            msgovrride_ref.current
        );
        setConvQ("");
        if (clearOnSend) setQ("");
    };

    const handleNewConverson = () => {
        setChatControlsOpen(false);
        newConverson();
    };
    const handleNewConverson_v2 = () => {
        setChatControlsOpen(false);
        setQ("");
        newConverson();
    };
    const handleToggleLeftPanel = () => {
        setChatControlsOpen(false);
        toggleLeftPanel();
    };
    const callOutVisibility = (val: boolean) => {};

    const sendQDisabled = disabled || !qRef.current.trim();

    const isOnCallbackFromSpeech = (isOn: boolean) => {
        setIsOn(isOn);
        isOnCallback(isOn);
    };
    const handleButtonClick = () => {
        setIsFileUploadDiagOpen(true);
    };
    const onRecognizing = (text: string, language: string) => {
        setConvQ(text);
        setQ(text);
    };
    const onRecognized = (
        text: string,
        language: string,
        current_intent: string,
        is_request_for_info?: Boolean,
        ignore_discard?: boolean,
        value_valid_bot_not_in_data?: string,
        msg_ovrride?: string
    ) => {
        if (is_request_for_info && is_request_for_info == true) window.globalRequestForInfo = true;

        ignore_discard_ref.current = ignore_discard || false;
        additional_instr.current = value_valid_bot_not_in_data || "";
        intent_ref.current = current_intent || "";
        msgovrride_ref.current = msg_ovrride || "";

        SetIterQ(prev => prev + 1);
        setConvQ(text);
        setQ(text);

        simulateSendClick();
    };

    const handleFileCheckboxChange = (fileid: string, checked: boolean) => {
        setFileMultiSelect(prev => prev.map(f => (f.fileid === fileid ? { ...f, selected: checked } : f)));
    };
    const finalizeFileSelection = () => {
        setIsFileMultiSelectCalloutOpen(false);
        handleSelectedFids(fileMultiSelect);

        console.log("fileMultiSelect", fileMultiSelect);
    };
    const finalizeAllSelection = () => {
        selectionRef.current?.setAllSelected(false);
        let fMultiSel: gfileuserselect[] = [];
        setFileMultiSelect(prev => {
            fMultiSel = prev.map(f => ({ ...f, selected: true }));
            return fMultiSel;
        });
        handleSelectedFids(fMultiSel);
        setIsFileMultiSelectCalloutOpen(false);
    };
    return (
        <Stack
            horizontal
            className={`${styles.questionInputContainer} ${
                (selectedGroup.premiumby ?? "") == "" && selectedGroup.dailymsgs > (lTiers?.at(0)?.msgsplmt || 0) ? styles.noMsggsConditional : ""
            }`}
        >
            {/* File Multi-Select Callout */}
            <Callout
                gapSpace={0}
                styles={questionInputUploadCallout}
                isBeakVisible={false}
                target={fileMultiSelectButRef.current}
                onDismiss={() => setIsFileMultiSelectCalloutOpen(false)}
                setInitialFocus
                directionalHint={DirectionalHint.leftTopEdge}
                className={styles.calloutMain}
                hidden={!isFileMultiSelectCalloutOpen}
            >
                <div style={{ minWidth: 320, maxWidth: 420, display: "flex", flexDirection: "column", maxHeight: 340 }}>
                    <div style={{ flex: 1, overflowY: "auto", minHeight: 120 }}>
                        <DetailsList
                            items={fileMultiSelect}
                            columns={fileColumns}
                            selection={selectionRef.current}
                            selectionMode={SelectionMode.multiple}
                            setKey="fileMultiSelectList"
                            checkboxVisibility={1} // on for selection
                            compact={true}
                            onRenderDetailsHeader={(props?: IDetailsHeaderProps) => onRenderDetailsHeader(props!)}
                            styles={{ root: { minHeight: 120 } }}
                            onItemInvoked={(item, index, event) => {
                                console.log("display_from_q", item);
                                onCitationClicked(`${item.name}#page=1_fid=${item.fileid}`, null);
                            }}
                        />
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: 8,
                            borderTop: "1px solid #eee",
                            background: "#fff",
                            position: "sticky",
                            bottom: 0,
                            zIndex: 1
                        }}
                    >
                        <PrimaryButton
                            style={{ width: "70%", borderRadius: "5px" }}
                            onClick={finalizeFileSelection}
                            disabled={fileMultiSelect.filter(f => f.selected).length === 0}
                        >
                            Select
                        </PrimaryButton>
                        <PrimaryButton style={{ width: "10%", marginLeft: "10px", borderRadius: "5px" }} onClick={finalizeAllSelection}>
                            All
                        </PrimaryButton>
                    </div>
                </div>
            </Callout>
            {/* Upload Callout */}
            <Callout
                gapSpace={0}
                styles={questionInputUploadCallout}
                isBeakVisible={false}
                target={uploadButRef.current}
                onDismiss={() => setIsFileUploadDiagOpen(false)}
                setInitialFocus
                directionalHint={DirectionalHint.leftTopEdge}
                className={styles.calloutMain}
                hidden={!isFileUploadDiagOpen}
            >
                <div className={styles.rightButtons}>
                    <div className={styles.libButs} id={`calloutButton_pdf`}>
                        <TooltipHost content="Upload Documents" directionalHint={DirectionalHint.topCenter}>
                            {/* Upload PDFs Button */}
                            <AddURLs
                                className={styles.commandButtonLib}
                                updateCatsState={updateCatsState}
                                butName="Documents"
                                calloutLabelText="Upload PDFs from your Device"
                                cTy="pdf"
                                listRefreshRequest={listRefreshRequest}
                                selectedGroup={selectedGroup}
                                callOutVisibility={callOutVisibility}
                                hideCallout={isFileUploadDiagOpen == false ? true : false}
                            />
                        </TooltipHost>
                    </div>
                    {selectedGroup.allowvideofiles == 1 && (
                        <div className={styles.libButs} id={`calloutButton_vid`}>
                            <TooltipHost content="Add Video Files" directionalHint={DirectionalHint.topCenter}>
                                {/* Upload Video Button */}
                                <AddURLs
                                    className={styles.commandButtonLib}
                                    updateCatsState={(newValue: string) => updateCatsState(newValue)}
                                    butName="Video"
                                    calloutLabelText="Upload Video Files from your Device"
                                    cTy="vid"
                                    listRefreshRequest={listRefreshRequest}
                                    selectedGroup={selectedGroup}
                                    callOutVisibility={callOutVisibility}
                                />
                            </TooltipHost>
                        </div>
                    )}
                    {selectedGroup.allowyoutube == 1 && (
                        <div className={styles.libButs} id={`calloutButton_youtube`}>
                            <TooltipHost content="Add YouTube videos" directionalHint={DirectionalHint.topCenter}>
                                {/* Upload Youtube Button */}
                                <AddURLs
                                    className={styles.commandButtonLib}
                                    updateCatsState={updateCatsState}
                                    butName="YouTube"
                                    calloutLabelText="Enter YouTube URL"
                                    cTy="youtube"
                                    listRefreshRequest={listRefreshRequest}
                                    selectedGroup={selectedGroup}
                                    callOutVisibility={callOutVisibility}
                                    hideCallout={isFileUploadDiagOpen ? false : true}
                                />
                            </TooltipHost>
                        </div>
                    )}
                    {selectedGroup.allowweb == 1 && (
                        <div className={styles.libButs} id={`calloutButton_web`}>
                            <TooltipHost content="Add Web Pages" directionalHint={DirectionalHint.topCenter}>
                                {/* Upload Web Button */}
                                <AddURLs
                                    className={styles.commandButtonLib}
                                    updateCatsState={updateCatsState}
                                    butName="Web"
                                    calloutLabelText="Enter Web URL"
                                    cTy="web"
                                    listRefreshRequest={listRefreshRequest}
                                    selectedGroup={selectedGroup}
                                    callOutVisibility={callOutVisibility}
                                    hideCallout={isFileUploadDiagOpen ? false : true}
                                />
                            </TooltipHost>
                        </div>
                    )}
                    {selectedGroup.allowaudiodocentry == 1 && (
                        <div className={styles.libButs} id={`calloutButton_mic`}>
                            {/* Record Audio Button */}
                            <TooltipHost content="Record Content" directionalHint={DirectionalHint.topCenter}>
                                <AddURLs
                                    className={styles.commandButtonLib}
                                    updateCatsState={updateCatsState}
                                    butName="Mic"
                                    calloutLabelText="Record Audio"
                                    cTy="mic"
                                    listRefreshRequest={listRefreshRequest}
                                    selectedGroup={selectedGroup}
                                    callOutVisibility={callOutVisibility}
                                />
                            </TooltipHost>
                        </div>
                    )}
                </div>
            </Callout>
            {/* NOT USED. Chat Controls Slide Out */}

            {chatControlsOpen && (
                <ChatControls
                    isOpen={chatControlsOpen}
                    onDismiss={() => setChatControlsOpen(false)}
                    ButtonRef={textAreaRef}
                    toggleLeftPanel={handleToggleLeftPanel}
                    newConverson={handleNewConverson}
                />
            )}
            {/* New Convo / Convo History Button Container */}
            <div ref={textAreaRef} className={styles.chatControlsContainer}>
                {/*New Conversation Button */}
                <ClearChatButton className={styles.commandButtonTwo} onClick={handleNewConverson_v2} />
                {/* Show Chat History Button */}
                <TogglePanelButton
                    className={styles.commandButton}
                    onClick={() => handleToggleLeftPanel()}
                    text=""
                    iconType={List28Regular}
                    tooltipText="View your Chat History"
                />
            </div>
            {/* Speech Button */}
            {selectedGroup.convomode == 1 && (
                <SpeechRecognizer
                    onRecognized={onRecognized}
                    onRecognizing={onRecognizing}
                    isOnCallback={isOnCallbackFromSpeech}
                    convoEnd={convoEnd}
                    selectedGroup={selectedGroup}
                    isSpeaking={isSpeakingState}
                    isSpeechPaused={isSpeechPaused}
                    lastMessage={lastMessage}
                    activeConvoKey={activeConvoKey}
                    question_and_answers_list={question_and_answers_list}
                    convo_discard_stamp={convo_discard_stamp}
                />
            )}
            <TextField
                id={"kgnfqinputfield"}
                className={styles.questionInputTextArea}
                styles={questionInputTextArea}
                autoAdjustHeight={true}
                placeholder={(selectedGroup.premiumby ?? "") == "" && selectedGroup.dailymsgs > (lTiers?.at(0)?.msgsplmt || 0) ? msgLmtHit : placeholder}
                multiline
                disabled={(selectedGroup.premiumby ?? "") == "" && selectedGroup.dailymsgs > (lTiers?.at(0)?.msgsplmt || 0) ? true : false}
                resizable={false}
                borderless
                value={qRef.current}
                onChange={onInputChange}
                onKeyDown={onEnterPress}
            />
            {/* Show Upload Callout Button */}
            {/* File Multi-Select Callout Button */}

            <div ref={fileMultiSelectButRef} className={styles.questionInputButtonsContainer}>
                <TooltipHost content="Select Content to Discuss. By default, all content is selected." directionalHint={DirectionalHint.topCenter}>
                    <ActionButton
                        className={styles.btn_action}
                        id="fileMultiSelectCalloutButton"
                        iconProps={{ iconName: undefined }}
                        styles={actionButtonStyles}
                        allowDisabledFocus
                        onClick={() => setIsFileMultiSelectCalloutOpen(true)}
                    >
                        <GoMultiSelect size={22} />
                    </ActionButton>
                </TooltipHost>
            </div>
            <div ref={uploadButRef} className={styles.questionInputButtonsContainerRight}>
                {permissionLevel > 1 && (
                    <TooltipHost content="Add new Content from your device, from web, youtube or other" directionalHint={DirectionalHint.topCenter}>
                        <ActionButton
                            className={styles.btn_action}
                            id="calloutButton"
                            iconProps={clipIcon}
                            styles={actionButtonStyles}
                            allowDisabledFocus
                            onClick={handleButtonClick}
                        ></ActionButton>
                    </TooltipHost>
                )}
            </div>
            {/* Send Message / Stop Streaming Button */}

            <div className={styles.questionInputButtonsContainer} ref={butContainerRef} key={`akg_${isOn}`}>
                {isStreaming == true ? (
                    <Button
                        size="large"
                        className={styles.sendButton}
                        icon={<Stop24Filled primaryFill="rgba(255, 255, 255, 0.6)" />}
                        onClick={isCancellingMidway}
                        style={{ backgroundColor: "#F1641E", borderRadius: "5px" }}
                    />
                ) : (
                    <Button
                        size="large"
                        id={"kgnfqsendbutton"}
                        ref={sendButRef}
                        className={styles.sendButton}
                        icon={<Send28Filled primaryFill="rgba(255, 255, 255, 0.6)" />}
                        disabled={sendQDisabled}
                        onClick={sendQ}
                        style={{ backgroundColor: "#F1641E", borderRadius: "5px" }}
                    />
                )}
                <div id={"kgnfqsendtarget"} className={styles.kgnfsndtrgt} onClick={sendConv} ref={convDivRef}></div>
            </div>
        </Stack>
    );
};
